import React, { useCallback, useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import CarouselButton from 'components/pages/home/carousel/CarouselButton';
import HeroPlayback from 'components/playback/HeroPlayback';
import focusManager from 'support/focus/FocusManager';
import { getStartDate } from 'support/mediaTileHelper';
import { parseDate } from 'common/support/dateHelper';
import {
  addRotatingSegmentTitleId,
  deleteRotatingSegmentTitleId,
} from 'actions/rotatingSegmentTitles';

import { useAuthN } from 'hooks/auth/subscriptions';

// import leftBackgroundImage from 'assets/images/heroLeftImage.svg';
import { VideoCtvRedux, KitcenSinkRowItemDataItem, RelatedItem } from 'store-types';

import styles from './CarouselItem.module.scss';
import { ChannelKey } from 'app-types';

const messages = {
  liveFlag: {
    id: 'tile.liveFlag',
    description: 'Text for live flag',
  },
  livePregameFlag: {
    id: 'tile.livePregameFlag',
    description: 'Text for live pregame flag',
  },
  livePostgameFlag: {
    id: 'tile.livePostgameFlag',
    description: 'Text for live postgame flag',
  },
  ctaUpcoming: {
    id: 'carousel.cta.upcoming',
    description: 'Text for upcoming cta',
  },
  ctaLive: {
    id: 'carousel.cta.live',
    description: 'Text for live cta',
  },
  dot: {
    id: 'common.dot',
    description: 'dot',
  },
  slideCounter: {
    id: 'carousel.tts.slideCounter',
    description: 'TTS for slide position',
  },
};

interface CarouselItemProps {
  onTileFocused?: () => void;
  focusId: string;
  className?: string | null;
  hideCarousel?: boolean;
  onError?: (error: unknown) => void;
  onClick?: (videoId?: string, isPlayable?: boolean, video?: KitcenSinkRowItemDataItem | RelatedItem | null) => void;
  handlePostGame?: (videoId: string) => void;
  video: KitcenSinkRowItemDataItem | RelatedItem | null;
  slideNum?: number | null;
  totalNum?: number | null;
}

const CarouselItem: React.FC<CarouselItemProps> = ({
  onTileFocused = () => {},
  focusId,
  className = '',
  hideCarousel = false,
  onError = () => {},
  onClick = () => {},
  handlePostGame = () => {},
  video,
  slideNum = null,
  totalNum = null,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const fullScreenPlayer = useSelector((state: VideoCtvRedux) => state.hero.fullScreenPlayer);
  const fullScreenVideoId = useSelector((state: VideoCtvRedux) => state.hero.videoId);
  const networks = useSelector((state: VideoCtvRedux) => state.kitchenSink.data?.networks);

  const [inFocus, setInFocus] = useState(false);
  const [showPlayIcon, setPlayIcon] = useState(true);
  const { about, event, properties, images } = video ?? {};

  const isPregame = event?.status === 'pre';
  const isPostgame = event?.status === 'post';
  const isUpcoming = (event?.status as string) === 'upcoming';

  const isPlayable = useAuthN(video?.authTypes);

  const dateString = getStartDate(
    intl,
    parseDate((video?.schedule?.videoStartDate as number) * 1000),
  );

  const src = images?.baseImage16X9;

  const brand = video?.network;
  const networkName = networks[brand as ChannelKey]?.name;
  const isLiveEvent = properties?.type === 'live';
  const title = isLiveEvent ? about?.title : networkName;

  const videoId = video?.id;

  const rotatingSegmentTitles = useSelector(
    (state: VideoCtvRedux) => state.rotatingSegmentTitles,
  );

  const rotatingSegmentIds = useSelector(
    (state: VideoCtvRedux) => state.updateRotatingSegmentTitles?.rotatingSegmentIds,
  );

  const segmentTitlesEnabled = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.data?.config?.modules?.SegmentTitles?.enabled,
  );

  const [shortTitle, setShortTitle] = useState<string | undefined | null>(about?.shortTitle);

  useEffect(() => {
    if (segmentTitlesEnabled && rotatingSegmentTitles?.data?.titleData) {
      if (
        Object.keys(rotatingSegmentTitles.data.titleData).find(
          key => key === video?.id,
        ) !== undefined
      ) {
        if (
          rotatingSegmentTitles?.data?.titleData[video?.id ?? '']?.segmentTitle !==
          null
        ) {
          setShortTitle(
            rotatingSegmentTitles?.data?.titleData[video?.id as string]?.segmentTitle,
          );
        }
        if (
          rotatingSegmentIds &&
          rotatingSegmentIds.indexOf(video?.id ?? '') === -1
        ) {
          dispatch(addRotatingSegmentTitleId(video?.id));
        }
      } else if (
        rotatingSegmentIds &&
        rotatingSegmentIds.indexOf(video?.id ?? '') !== -1
      ) {
        dispatch(deleteRotatingSegmentTitleId(video?.id ?? ''));
      }
    }
  }, [
    dispatch,
    segmentTitlesEnabled,
    rotatingSegmentTitles,
    rotatingSegmentIds,
    video,
  ]);

  useEffect(() => {
    if (
      fullScreenPlayer &&
      videoId === fullScreenVideoId &&
      !hideCarousel &&
      video?.event?.status === 'post'
    ) {
      handlePostGame?.(videoId);
    }
  });

  const getSubtitleText = () => {
    if (isLiveEvent) {
      if (isUpcoming) {
        return about?.description;
      }
      return `${dateString} ${about?.description}`;
    }
    return about?.segmentTitle || shortTitle;
  };

  const getCTAText = () => {
    if (isUpcoming) {
      return intl.formatMessage(messages.ctaUpcoming);
    }
    return intl.formatMessage(messages.ctaLive);
  };

  const getBadgeText = () => {
    if (isPregame) {
      return intl.formatMessage(messages.livePregameFlag);
    }
    if (isPostgame) {
      return intl.formatMessage(messages.livePostgameFlag);
    }
    return intl.formatMessage(messages.liveFlag);
  };

  const getSlideNumText = () => {
    if (slideNum && totalNum) {
      return intl.formatMessage(messages.slideCounter, {
        index: slideNum,
        total: totalNum,
      });
    }
    return null;
  };

  const onFocusSet = useCallback(
    (value: boolean) => {
      if (value) {
        setInFocus(true);
        onTileFocused?.();
      }
    },
    [onTileFocused],
  );

  useEffect(() => {
    const onFocusChangedListener = (_newId: string | number | null, prevId: string | number | null) => {
      if (prevId === focusId) {
        setInFocus(false);
      }
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusId]);

  useEffect(() => {
    if (isUpcoming) {
      setPlayIcon(false);
    } else {
      setPlayIcon(true);
    }
  }, [isUpcoming]);

  const showPlayer =
    (inFocus || (fullScreenPlayer && videoId === fullScreenVideoId)) &&
    !hideCarousel;

  const handleOnClick = useCallback(() => {
    onClick?.(videoId, isPlayable, video);
  }, [videoId, isPlayable, onClick, video]);

  const tts = [title, getSubtitleText(), getCTAText(), getSlideNumText()].join(
    ', ',
  );

  return (
    <div className={classNames(styles.carouselImage, 'w-100', 'h-100')}>
      {!fullScreenPlayer && (
        <div
          className={styles.rightBackground}
          style={{ backgroundImage: `url(${src})` }}
        />
      )}
      {!fullScreenPlayer && (
        <div
          className={styles.leftBackground}
          // style={{ backgroundImage: `url(${leftBackgroundImage})` }}
        />
      )}
      {!fullScreenPlayer && <div className={styles.bottomGradient} />}

      <div
        className={classNames(
          className,
          !fullScreenPlayer && styles.carouselContent,
          'd-flex',
          'flex-column',
          'carouselSlide',
        )}
      >
        {!fullScreenPlayer && isLiveEvent && (
          <div className={classNames(styles.liveEventHeader)}>
            <span className={classNames(styles.prefix)}>{about?.prefix}</span>
            {about?.prefix && networkName
              ? intl.formatMessage(messages.dot)
              : null}
            <span className={classNames(styles.network)}>{networkName}</span>
          </div>
        )}
        {!fullScreenPlayer && (
          <>
            <div className={classNames(styles.title)}>{title}</div>
            <div className={styles.subtitle}>
              {!isUpcoming && (
                <span className={styles.liveBadge} data-testid="live-badge">
                  {getBadgeText()}
                </span>
              )}
              {isUpcoming && (
                <span className={styles.startDate} data-testid="upcoming-time">
                  {dateString}
                </span>
              )}
              <div
                className={classNames(
                  styles.subtitleText,
                  'carousel-item-subtitle',
                )}
              >
                <div
                  className={classNames(
                    styles.subtitleText,
                    'carousel-item-subtitle',
                  )}
                >
                  {getSubtitleText()}
                </div>
              </div>
            </div>
          </>
        )}
        {!fullScreenPlayer && (
          <CarouselButton
            focusId={focusId}
            className={classNames(styles.carouselCta, 'carousel-item-cta')}
            onClick={handleOnClick}
            onFocusSet={onFocusSet}
            size="md"
            ttsText={tts}
            showPlayIcon={showPlayIcon}
          >
            {getCTAText()}
          </CarouselButton>
        )}
        {showPlayer && (
          <div
            className={classNames(
              styles.player,
              fullScreenPlayer ? styles.playerLarge : styles.playerSmall,
            )}
          >
            <HeroPlayback
              videoId={videoId}
              id={focusId}
              inFocus={inFocus}
              isCarousel
              fullScreenPlayer={fullScreenPlayer}
              onError={onError}
              slideNum={slideNum}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CarouselItem;
